import { FC, useMemo } from 'react';

import { Transaction } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Checkbox } from '@ravago/shared/radiance/components/checkbox/components/Checkbox/Checkbox';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableHead } from '@ravago/shared/radiance/components/table/components/TableHead/TableHead';
import { TableHeader } from '@ravago/shared/radiance/components/table/components/TableHeader/TableHeader';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { canSelectInvoice } from '../../../../../utils/invoice.utils';

import { OpenInvoiceTableRow } from './open-invoice-table-row';

type Props = {
   currency: string;
   invoices: Transaction[];
   canPayOnline: boolean;
   selectedInvoiceIds: string[];
   onInvoiceSelect: (invoiceId: string) => void;
   onInvoiceDeselect: (invoiceId: string) => void;
   onSelectAll: (invoiceIds: string[]) => void;
   onDeselectAll: (invoiceIds: string[]) => void;
};

export const OpenInvoiceTableDesktop: FC<Props> = ({
   currency,
   invoices,
   canPayOnline,
   selectedInvoiceIds,
   onInvoiceSelect,
   onInvoiceDeselect,
   onSelectAll,
   onDeselectAll
}) => {
   const translate = useTranslations();

   const selectableInvoices = invoices.filter(canSelectInvoice);

   const { checked, indeterminate } = useMemo(() => {
      const containsAll =
         selectableInvoices.length > 0 && selectableInvoices.every((selectableInvoice) => selectedInvoiceIds.includes(selectableInvoice.id));
      return {
         checked: containsAll,
         indeterminate: !containsAll && selectableInvoices.some((selectableInvoice) => selectedInvoiceIds.includes(selectableInvoice.id))
      };
   }, [selectableInvoices, selectedInvoiceIds]);

   const canSelectAll = selectableInvoices.length > 0;

   const handleSelectAllChange = (selected: boolean) => {
      const selectableInvoiceIds = selectableInvoices.map(({ id }) => id);
      if (selected) {
         onSelectAll(selectableInvoiceIds);
      } else {
         onDeselectAll(selectableInvoiceIds);
      }
   };

   return (
      <Box direction="column">
         <Table>
            <TableHead>
               <TableRow>
                  {canPayOnline && (
                     <TableHeader>
                        <Checkbox disabled={!canSelectAll} checked={checked} indeterminate={indeterminate} onChange={handleSelectAllChange} />
                     </TableHeader>
                  )}
                  <TableHeader>
                     <Typography variant="small-highlight">{translate('invoices.open-invoices.table.table-header.invoice')}</Typography>
                  </TableHeader>
                  <TableHeader>
                     <Typography variant="small-highlight">{translate('invoices.open-invoices.table.table-header.date')}</Typography>
                  </TableHeader>
                  <TableHeader>
                     <Typography variant="small-highlight">{translate('invoices.open-invoices.table.table-header.due-date')}</Typography>
                  </TableHeader>
                  <TableHeader align="right">
                     <Typography variant="small-highlight">
                        {translate('invoices.open-invoices.table.table-header.open-amount', { currency })}
                     </Typography>
                  </TableHeader>
                  <TableHeader>{/* Placeholder */}</TableHeader>
                  <TableHeader>{/* Placeholder */}</TableHeader>
               </TableRow>
            </TableHead>
            <TableBody>
               {invoices.map((invoice, index) => (
                  <OpenInvoiceTableRow
                     // eslint-disable-next-line @eslint-react/no-array-index-key
                     key={index}
                     invoice={invoice}
                     canPayOnline={canPayOnline}
                     selected={selectedInvoiceIds.includes(invoice.id)}
                     onSelect={() => onInvoiceSelect(invoice.id)}
                     onDeselect={() => onInvoiceDeselect(invoice.id)}
                  />
               ))}
            </TableBody>
         </Table>
      </Box>
   );
};
